import { AdCuePoint } from 'components/pages/playback/ad-managers/AbstractAdManager';
import { BcPlayer } from './brightcove';

/**
 * IMA stream events, see https://developers.google.com/ad-manager/dynamic-ad-insertion/sdk/html5/reference/js/StreamEvent
 */
export enum ImaEvent {
  LOADED = 'loaded',
  AD_BREAK_STARTED = 'adBreakStarted',
  AD_BREAK_ENDED = 'adBreakEnded',
  AD_PERIOD_STARTED = 'adPeriodStarted',
  AD_PERIOD_ENDED = 'adPeriodEnded',
  AD_PROGRESS = 'adProgress',
  CUEPOINTS_CHANGED = 'cuepointsChanged',
  CLICK = 'click',
  ERROR = 'error',
  STARTED = 'started',
  FIRST_QUARTILE = 'firstquartile',
  MIDPOINT = 'midpoint',
  STREAM_INITIALIZED = 'streamInitialized',
  THIRD_QUARTILE = 'thirdquartile',
  COMPLETE = 'complete',
  SKIPPABLE_STATE_CHANGED = 'skippableStateChanged',
  SKIPPED = 'skip',
  VIDEO_CLICKED = 'videoClicked',
  PAUSED = 'paused',
  RESUMED = 'resumed',
}

// BcPlayer is a declaration of a class (not a class itself) so we need to define the events
export const BcPlayerEvents: (typeof BcPlayer)['Event'] = {
  PlayerStateChanged: 'playerStateChanged',
  PlayerMutedStatusChanged: 'playerMutedStatusChanged',
  PlayerVolumeChanged: 'playerVolumeChanged',
  PlayerTimeChanged: 'playerTimeChanged',
  PlayerDurationChanged: 'playerDurationChanged',
  PlayerRateChanged: 'playerRateChanged',
  PlayerEnded: 'playerEnded',
  PlayerError: 'playerError',
  PlayerVideoElementChanged: 'playerVideoElementChanged',
  PlayerPlay: 'playerPlay',
  PlayerPlaying: 'playerPlaying',
  PlayerPause: 'playerPause',
  PlayerWaiting: 'playerWaiting',
  PlayerDisposed: 'playerDisposed',
  PlayerLoadedMetadata: 'playerLoadedMetadata',
  PlayerLoadedData: 'playerLoadedData',
  PlayerLoadStart: 'playerLoadStart',
  PlayerCanPlay: 'playerCanPlay',
  PlayerCanPlayThrough: 'playerCanplayThrough',
  PlayerProgress: 'playerProgress',
  PlayerSeeking: 'playerSeeking',
  PlayerSeeked: 'playerSeeked',
  PlayerAbort: 'playerAbort',
  PlayerEmptied: 'playerEmptied',
  PlayerStalled: 'playerStalled',
  PlayerSuspend: 'playerSuspend',
  PlayerResize: 'playerResize',
  PlayerTextTracksChanged: 'playerTextTracksChanged',
  PlayerThumbnailTracksChanged: 'playerThumbnailTracksChanged',
  PlayerAudioTracksChanged: 'playerAudioTracksChanged',
  PlayerQualityLevelsChanged: 'playerQualityLevelsChanged',
  PlayerEnablePictureInPicture: 'playerEnablePictureInPicture',
  PlayerDisablePictureInPicture: 'playerDisablePictureInPictureChanged',
  PlayerEnterPictureInPicture: 'playerEnterPictureInPicture',
  PlayerExitPictureInPicture: 'playerExitPictureInPicture',
  ManifestRequestStart: 'manifestrequeststart',
  ManifestRequestComplete: 'manifestrequestcomplete',
  ManifestParseStart: 'manifestparsestart',
  ManifestParseComplete: 'manifestparsecomplete',
  PlaylistRequestStart: 'playlistrequeststart',
  PlaylistRequestComplete: 'playlistrequestcomplete',
  PlaylistParseStart: 'playlistparsestart',
  PlaylistParseComplete: 'playlistparsecomplete',
  SegmentSelected: 'segmentselected',
  SegmentLoadStart: 'segmentloadstart',
  SegmentLoaded: 'segmentloaded',
  SegmentKeyLoadStart: 'segmentkeyloadstart',
  SegmentKeyLoadComplete: 'segmentkeyloadcomplete',
  SegmentDecryptionStart: 'segmentdecryptionstart',
  SegmentDecryptionComplete: 'segmentdecryptioncomplete',
  SegmentTransmuxingStart: 'segmenttransmuxingstart',
  SegmentTransmuxingComplete: 'segmenttransmuxingcomplete',
  SegmentTransmuxingTrackInfoAvailable: 'segmenttransmuxingtrackinfoavailable',
  SegmentTransmuxingTimingInfoAvailable:
    'segmenttransmuxingtiminginfoavailable',
  SegmentAppendStart: 'segmentappendstart',
  SegmentAppendComplete: 'segmentappendcomplete',
  RenditionDisabled: 'renditiondisabled',
  RenditionEnabled: 'renditionenabled',
  RenditionSelected: 'renditionselected',
  GapJumped: 'gapjumped',
  BandwidthUpdate: 'bandwidthupdate',
  TimelineChange: 'timelinechange',
  CodecsChange: 'codecschange',
  SeekableRangesChanged: 'seekablerangeschanged',
  BufferedRangesChanged: 'bufferedrangeschanged',
  PlayedRangesChanged: 'playedrangeschanged',
  ContentSteeringLoadStart: 'contentSteeringloadstart',
  ContentSteeringLoadComplete: 'contentsteeringloadcomplete',
  ContentSteeringParsed: 'contentsteeringparsed',
  EMEKeySystemAccessCompleted: 'emeKeySystemAccessCompleted',
  EMEKeySessionCreated: 'emeKeySessionCreated',
  EMEKeySessionUpdated: 'emeKeySessionUpdated',
  EMEKeySessionClosed: 'emeKeySessionClosed',
  EMEKeySessionRemoved: 'emeKeySessionRemoved',
  EMEKeyStatusesChanged: 'emeKeyStatusesChanged',
  EMEKeyMessage: 'emeKeyMessage',
  EMELicenseRequestAttempted: 'emeLicenseRequestAttempted',

  Ads: {
    ContentChanged: 'contentchanged',
    ContentUpdate: 'contentUpdate',
    ContentResumed: 'contentresumed',
    Ready: 'adsready',
    Cancelled: 'adscancelled',
    ReadyForPreroll: 'adsReadyForPreroll',
    ReadyForPostroll: 'adsReadyForPostroll',
    NoPreroll: 'adsNoPreroll',
    NoPostroll: 'adsNoPostroll',
    AdStart: 'adStart',
    AdEnd: 'adEnd',
    AdSkip: 'adskip',
    AdTimeout: 'adTimeout',
  },

  Integrations: {
    ImaDai: {
      ImaDaiSdkLoaded: 'imaDaiSdkLoaded',
    },
    Pinning: {
      BeforePinningEnabled: 'beforepinenabled',
      PinningEnabled: 'pinenabled',
      BeforePinningDisabled: 'beforepindisabled',
      PinningDisabled: 'pindisabled',
      BeforePin: 'beforepin',
      Pin: 'pin',
      BeforeUnpin: 'beforeunpin',
      Unpin: 'unpin',
      BeforePinClose: 'beforepinclose',
      PinClose: 'pinclose',
    },
    ImaClientSide: {
      ImaClientSideAdClick: 'imaClientSideAdClick',
      ImaClientSideAdStarted: 'imaClientSideAdStarted',
      ImaClientSideAdComplete: 'imaClientSideAdComplete',
      ImaClientSideFirstQuartile: 'imaClientSideFirstQuartile',
      ImaClientSideMidPoint: 'imaClientSideMidPoint',
      ImaClientSideThirdQuartile: 'imaClientSideThirdQuartile',
      ImaClientSideVolumeChanged: 'imaClientSideVolumeChanged',
      ImaClientSidePaused: 'imaClientSidePaused',
      ImaClientSideResumed: 'imaClientSideResumed',
      ImaClientSideSkipped: 'imaClientSideSkipped',
      ImaClientSideAllAdsCompleted: 'imaClientSideAllAdsCompleted',
      ImaClientSideAdError: 'imaClientSideAdError',
      ImaClientSideSdkLoaded: 'imaClientSideSdkLoaded',
      ImaClientSideAdsRequest: 'imaClientSideAdsRequest',
      ImaClientSideAdsPodEnded: 'imaClientSideAdsPodEnded',
      ImaClientSideAdsPodStarted: 'imaClientSideAdsPodStarted',
      ImaClientSideAdsLoad: 'imaClientSideAdsLoad',
    },
    Ssai: {
      ClickThrough: 'ssaiClickThrough',
    },
  },

  SpatialNavigation: {
    FocusableComponentsChanged: 'focusableComponentsChanged',
    EndOfFocusableComponents: 'endOfFocusableComponents',
  },
};

/**
 * Custom player events:
 * - `BEFORE_SEEK`: Called before the player seeks
 * - `SKIP_AD_POD`: Called when skipping an ad pod that has already been watched
 * - `CLEANUP_AD_POD`: Called when an ad finishes but there is no AD_BREAK_ENDED event
 */
export enum CustomPlayerEvent {
  SEEK_PAUSE = 'seekPause',
  BEFORE_SEEK = 'beforeSeek',
  SKIP_AD_POD = 'skipAdPod',
  CLEANUP_AD_POD = 'cleanupAdPod',
  SEEKING = 'seeking', // custom event used for brightcove seeking
  UNLOADING = 'unloading',
}

// see: https://developers.google.com/interactive-media-ads/docs/sdks/html5/dai/reference/js/Ad
export type Ad = {
  getAdId: () => string;
  getAdPodInfo: () => AdPodInfo;
  getAdSystem: () => string;
  getApiFramework: () => string | null;
  getCreativeId: () => string;
  getDuration: () => number;
  getTitle: () => string;
  getWrapperAdIds: () => string[];
  getWrapperAdSystems: () => string[];
  getWrapperCreativeIds: () => string[];
  adStitcher?: () => string;
};

export type AdPodInfo = {
  getPodIndex: () => number;
  getAdPosition: () => number;
  [key: string]: () => unknown;
};

export type ImaStreamManager = {
  reset: () => void;
  requestStream: (stream: unknown) => void;
  streamTimeForContentTime: (time: number) => number;
  contentTimeForStreamTime: (time: number) => number;
  previousCuePointForStreamTime: (currentTime: any) => AdCuePoint | null;
  addEventListener: (
    type: string,
    callback: (event: any) => void,
    bool?: boolean,
  ) => void;
  removeEventListener: (type: string, callback: (event: any) => void) => void;
};
